import { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { useTheme } from '../../context/ThemeContext'; // Ajuste o caminho
import { 
  updatePassword, 
  reauthenticateWithCredential, 
  EmailAuthProvider,
  deleteUser,
  signOut 
} from 'firebase/auth';
import { doc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { FiStar, FiDollarSign, FiAlertTriangle, FiPhoneCall, FiCpu, FiCheckSquare, FiSquare, FiCopy, FiPrinter } from 'react-icons/fi';
import { FaAndroid, FaApple } from 'react-icons/fa';
import { generateAsoText } from '../../services/aiService';

const SettingsPanel = ({ 
  notificationSettings, 
  handleSettingChange, 
  copyToClipboard,
  showToast,
  userApps
}) => {
  const triggerToast = (msg, type = 'success') => {
    if (showToast) {
      showToast(msg, type);
    } else {
      alert(msg);
    }
  };

  const [qrCodeUrl, setQrCodeUrl] = useState(window.location.origin);

  useEffect(() => {
    if (userApps && userApps.length > 0 && userApps[0].url) {
      const url = userApps[0].url.trim();
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
      setQrCodeUrl(formattedUrl);
    }
  }, [userApps]);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const { isDarkMode, toggleTheme } = useTheme();

  // ASO Checklist state
  const [asoTasks, setAsoTasks] = useState({
    titleKeywords: false,
    longDescription: false,
    screenshots: false,
    smartBanner: false
  });

  // ASO AI Generator state
  const [asoInputs, setAsoInputs] = useState({
    appName: '',
    category: 'E-commerce',
    keywords: '',
    platform: 'Lojas de Apps'
  });
  const [asoLoading, setAsoLoading] = useState(false);
  const [asoResult, setAsoResult] = useState('');

  const handleToggleAsoTask = (taskKey) => {
    setAsoTasks(prev => ({
      ...prev,
      [taskKey]: !prev[taskKey]
    }));
  };

  const handleGenerateAso = async (e) => {
    e.preventDefault();
    if (!asoInputs.appName.trim()) {
      triggerToast('Por favor, informe o nome do aplicativo.', 'error');
      return;
    }
    setAsoLoading(true);
    try {
      const result = await generateAsoText(
        asoInputs.appName,
        asoInputs.category,
        asoInputs.keywords,
        asoInputs.platform
      );
      setAsoResult(result);
    } catch (error) {
      console.error('Error generating ASO text', error);
    } finally {
      setAsoLoading(false);
    }
  };

  const handlePrintQrCode = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Imprimir QR Code - \${asoInputs.appName || 'Meu App'}</title>
          <style>
            body { font-family: 'Inter', sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; }
            .container { text-align: center; border: 3px solid #000; padding: 40px; border-radius: 20px; max-width: 400px; }
            h1 { margin-bottom: 10px; font-size: 28px; }
            p { color: #666; margin-bottom: 30px; font-size: 16px; }
            .qr-placeholder { width: 250px; height: 250px; background: #fff; display: flex; align-items: center; justify-content: center; margin: 0 auto 30px; border-radius: 10px; border: 1px solid #eee; overflow: hidden; }
            .btn { display: none; }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Baixe nosso App!</h1>
            <p>Aponte a câmera para o QR Code abaixo</p>
            <div class="qr-placeholder">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrCodeUrl)}" alt="QR Code" style="width: 100%; height: 100%; object-fit: contain;" />
            </div>
            <h2>\${asoInputs.appName || 'Nosso Aplicativo'}</h2>
            <button class="no-print" onclick="window.print()">Imprimir</button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const completedCount = Object.values(asoTasks).filter(Boolean).length;
  const asoScore = 40 + completedCount * 15; // de 40% a 100%

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      triggerToast('As senhas não coincidem!', 'error');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      triggerToast('A nova senha deve ter pelo menos 6 caracteres!', 'error');
      setLoading(false);
      return;
    }

    try {
      const user = auth.currentUser;
      
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);
      
      await updatePassword(user, passwordData.newPassword);
      
      triggerToast('Senha alterada com sucesso!', 'success');
      setChangePasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      
      if (error.code === 'auth/wrong-password') {
        triggerToast('Senha atual incorreta!', 'error');
      } else if (error.code === 'auth/requires-recent-login') {
        triggerToast('Por favor, faça login novamente antes de alterar a senha.', 'error');
      } else {
        triggerToast('Erro ao alterar senha: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm.toLowerCase() !== 'excluir minha conta') {
      triggerToast('Por favor, digite "excluir minha conta" para confirmar.', 'error');
      return;
    }

    if (!window.confirm('⚠️ ATENÇÃO: Esta ação é irreversível!\n\nTem certeza absoluta que deseja excluir sua conta?')) {
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      
      const appsQuery = query(
        collection(db, 'apps'),
        where('userId', '==', user.uid)
      );
      
      const appsSnapshot = await getDocs(appsQuery);
      const batch = writeBatch(db);
      
      appsSnapshot.forEach((appDoc) => {
        batch.delete(appDoc.ref);
      });
      
      await batch.commit();
      
      await deleteDoc(doc(db, 'users', user.uid));
      
      await deleteUser(user);
      
      triggerToast('Conta excluída com sucesso. Você será desconectado.', 'success');
      await signOut(auth);
      
    } catch (error) {
      console.error('Erro ao excluir conta:', error);
      
      if (error.code === 'auth/requires-recent-login') {
        triggerToast('Por favor, faça login novamente antes de excluir a conta.', 'error');
      } else {
        triggerToast('Erro ao excluir conta: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
      setDeleteAccountModal(false);
      setDeleteConfirm('');
    }
  };

  return (
    <div className="space-y-6">
      {/* NOVA SEÇÃO: Configurações de Tema */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-none shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Preferências de Tema
        </h3>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-white">Modo Escuro</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isDarkMode ? 'Tema escuro ativado' : 'Tema claro ativado'}
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className={`
              relative inline-flex items-center h-6 rounded-full w-11
              transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              ${isDarkMode ? 'bg-blue-600' : 'bg-gray-200'}
            `}
          >
            <span className="sr-only">Alternar tema</span>
            <span
              className={`
                inline-block w-4 h-4 transform bg-white rounded-full transition-transform
                ${isDarkMode ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>

      {/* Configurações de Notificação */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-none shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Preferências de Notificação</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-800 dark:text-white">Receber notificações</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Permitir que enviemos notificações importantes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={notificationSettings.receiveNotifications}
                onChange={(e) => handleSettingChange('receiveNotifications', e.target.checked)}
                disabled
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 opacity-50"></div>
            </label>
          </div>
          
          <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">Painel de ASO e Marketing</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Ferramentas completas para aumentar as buscas orgânicas e impulsionar os downloads do seu aplicativo.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Coluna 1: ASO Score & Auditor */}
              <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 rounded-none p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h4 className="font-bold text-gray-800 dark:text-white">Auditoria ASO do App</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Otimização para busca orgânica</p>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">{asoScore}%</span>
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Score ASO</span>
                  </div>
                </div>

                {/* Barra de Progresso ASO */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden mb-6">
                  <div 
                    className="bg-gradient-to-r from-emerald-400 to-teal-500 h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${asoScore}%` }}
                  ></div>
                </div>

                {/* Lista de Checkbox ASO */}
                <div className="space-y-3">
                  <button 
                    onClick={() => handleToggleAsoTask('titleKeywords')}
                    className="w-full flex items-center justify-between p-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-none hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      {asoTasks.titleKeywords ? (
                        <FiCheckSquare className="text-emerald-500 w-5 h-5 flex-shrink-0" />
                      ) : (
                        <FiSquare className="text-gray-400 w-5 h-5 flex-shrink-0" />
                      )}
                      <div>
                        <p className={`text-sm font-medium ${asoTasks.titleKeywords ? 'text-gray-500 line-through' : 'text-gray-800 dark:text-white'}`}>
                          Palavras-chave no título
                        </p>
                        <p className="text-[11px] text-gray-400">Inserir termos de busca no título do app</p>
                      </div>
                    </div>
                  </button>

                  <button 
                    onClick={() => handleToggleAsoTask('longDescription')}
                    className="w-full flex items-center justify-between p-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-none hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      {asoTasks.longDescription ? (
                        <FiCheckSquare className="text-emerald-500 w-5 h-5 flex-shrink-0" />
                      ) : (
                        <FiSquare className="text-gray-400 w-5 h-5 flex-shrink-0" />
                      )}
                      <div>
                        <p className={`text-sm font-medium ${asoTasks.longDescription ? 'text-gray-500 line-through' : 'text-gray-800 dark:text-white'}`}>
                          Descrição longa otimizada
                        </p>
                        <p className="text-[11px] text-gray-400">Escrever descrição com recursos organizados</p>
                      </div>
                    </div>
                  </button>

                  <button 
                    onClick={() => handleToggleAsoTask('screenshots')}
                    className="w-full flex items-center justify-between p-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-none hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      {asoTasks.screenshots ? (
                        <FiCheckSquare className="text-emerald-500 w-5 h-5 flex-shrink-0" />
                      ) : (
                        <FiSquare className="text-gray-400 w-5 h-5 flex-shrink-0" />
                      )}
                      <div>
                        <p className={`text-sm font-medium ${asoTasks.screenshots ? 'text-gray-500 line-through' : 'text-gray-800 dark:text-white'}`}>
                          Capturas de tela das lojas
                        </p>
                        <p className="text-[11px] text-gray-400">Enviar de 3 a 5 prints do app rodando</p>
                      </div>
                    </div>
                  </button>

                  <button 
                    onClick={() => handleToggleAsoTask('smartBanner')}
                    className="w-full flex items-center justify-between p-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-none hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      {asoTasks.smartBanner ? (
                        <FiCheckSquare className="text-emerald-500 w-5 h-5 flex-shrink-0" />
                      ) : (
                        <FiSquare className="text-gray-400 w-5 h-5 flex-shrink-0" />
                      )}
                      <div>
                        <p className={`text-sm font-medium ${asoTasks.smartBanner ? 'text-gray-500 line-through' : 'text-gray-800 dark:text-white'}`}>
                          Smart Banner no site original
                        </p>
                        <p className="text-[11px] text-gray-400">Atrair tráfego mobile do site para baixar o app</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Coluna 2: Gerador ASO de IA */}
              <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 rounded-none p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FiCpu className="text-gray-800 dark:text-gray-200 w-5 h-5" />
                    <h4 className="font-bold text-gray-800 dark:text-white">Gerador ASO com IA</h4>
                  </div>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                    Crie títulos, subtítulos e descrições otimizados com inteligência artificial para cadastrar nas lojas (Google Play e App Store), melhorando suas buscas orgânicas.
                  </p>

                  <form onSubmit={handleGenerateAso} className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                        Nome do Aplicativo
                      </label>
                      <input 
                        type="text"
                        value={asoInputs.appName}
                        onChange={(e) => setAsoInputs({...asoInputs, appName: e.target.value})}
                        placeholder="Ex: Minha Loja Virtual"
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                          Plataforma
                        </label>
                        <select
                          value={asoInputs.platform}
                          onChange={(e) => setAsoInputs({...asoInputs, platform: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="Lojas de Apps">Lojas de Apps (Play/App Store)</option>
                          <option value="Instagram">Instagram</option>
                          <option value="Facebook">Facebook</option>
                          <option value="WhatsApp">WhatsApp</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                          Categoria
                        </label>
                        <select
                          value={asoInputs.category}
                          onChange={(e) => setAsoInputs({...asoInputs, category: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="E-commerce">E-commerce</option>
                          <option value="Delivery">Delivery</option>
                          <option value="Serviços">Serviços</option>
                          <option value="Blog/Notícias">Blog / Portal</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                          Palavras-chave
                        </label>
                        <input 
                          type="text"
                          value={asoInputs.keywords}
                          onChange={(e) => setAsoInputs({...asoInputs, keywords: e.target.value})}
                          placeholder="Ex: compras, cupom"
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={asoLoading}
                      className="w-full py-2.5 bg-gray-900 hover:bg-black text-white dark:bg-white dark:text-black dark:hover:bg-gray-100 rounded-none text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      {asoLoading ? 'Gerando textos...' : 'Gerar Metadados Otimizados'}
                    </button>
                  </form>
                </div>

                {asoResult && (
                  <div className="mt-4 p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-none flex-grow">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Resultado ({asoInputs.platform})</span>
                      <button 
                        onClick={() => copyToClipboard(asoResult)}
                        className="text-xs text-gray-500 hover:text-black dark:hover:text-white flex items-center gap-1 font-semibold transition-colors"
                      >
                        <FiCopy /> Copiar Tudo
                      </button>
                    </div>
                    <pre className="text-xs text-gray-600 dark:text-gray-300 font-mono whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed custom-scrollbar bg-gray-50 dark:bg-gray-900 p-2.5 rounded-none border border-gray-100 dark:border-gray-800">
                      {asoResult}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Subseção: Kit de Divulgação */}
            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
              <h4 className="font-bold text-gray-800 dark:text-white mb-4">Kit de Divulgação (Marketing)</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* QR Code Card */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none p-5 flex flex-col md:flex-row items-center gap-6">
                  {/* Real QR Code */}
                  <div className="w-24 h-24 bg-white rounded-none p-1.5 flex-shrink-0 flex items-center justify-center border border-gray-100 dark:border-gray-700">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCodeUrl)}`} 
                      alt="QR Code" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-grow w-full">
                    <h5 className="font-bold text-gray-800 dark:text-white text-sm">QR Code de Download</h5>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed mb-3">
                      Link de destino desejado (seu site ou link de download):
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input 
                        type="url"
                        value={qrCodeUrl}
                        onChange={(e) => setQrCodeUrl(e.target.value)}
                        placeholder="Ex: https://meusite.com.br"
                        className="flex-grow px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-none focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                      <button 
                        onClick={handlePrintQrCode}
                        className="text-xs bg-black hover:bg-gray-900 dark:bg-white dark:hover:bg-gray-100 dark:text-black text-white font-semibold py-1.5 px-3 rounded-none transition-colors flex items-center gap-1.5 shadow-sm justify-center"
                      >
                        <FiPrinter /> Imprimir QR Code
                      </button>
                    </div>
                  </div>
                </div>

                {/* Smart Banner Card */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none p-5 flex flex-col justify-between">
                  <div>
                    <h5 className="font-bold text-gray-800 dark:text-white text-sm">Smart Banner para o Site</h5>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      Adicione este código HTML no seu site para mostrar um banner inteligente oferecendo o app aos usuários de celular.
                    </p>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 p-2 bg-gray-50 dark:bg-gray-900 rounded-none border border-gray-100 dark:border-gray-800">
                    <code className="text-[10px] text-gray-600 dark:text-gray-400 font-mono truncate select-all">
                      {`<!-- CriaApp Banner -->\n<div class="criaapp-smart-banner" data-app="${asoInputs.appName || 'app'}"></div>`}
                    </code>
                    <button 
                      onClick={() => copyToClipboard(`<!-- CriaApp Banner -->\n<div class="criaapp-smart-banner" data-app="${asoInputs.appName || 'app'}"></div>`)}
                      className="text-xs text-gray-800 dark:text-white hover:bg-gray-200/50 p-1.5 rounded-none flex-shrink-0 transition-colors"
                      title="Copiar Código"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Links de Download */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-none shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Links de Download</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">Compartilhe esses links para que seus usuários possam baixar seu aplicativo</p>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-none">
          <div className="flex items-center">
              <div className="bg-green-100 dark:bg-green-900 p-2 rounded-none mr-3">
                <FaAndroid className="text-green-600 dark:text-green-400 w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white">Google Play Store</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Link para download na Play Store</p>
              </div>
            </div>
            <button 
              className="px-3.5 py-1.5 bg-black hover:bg-gray-900 dark:bg-white dark:hover:bg-gray-100 dark:text-black text-white rounded-none text-xs font-semibold transition-colors"
              onClick={() => copyToClipboard('https://play.google.com/store/apps/details?id=com.seuapp')}
            >
              Copiar Link
            </button>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-none">
          <div className="flex items-center">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-none mr-3">
                <FaApple className="text-blue-600 dark:text-blue-400 w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white">Apple App Store</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Link para download na App Store</p>
              </div>
            </div>
            <button 
              className="px-3.5 py-1.5 bg-black hover:bg-gray-900 dark:bg-white dark:hover:bg-gray-100 dark:text-black text-white rounded-none text-xs font-semibold transition-colors"
              onClick={() => copyToClipboard('https://apps.apple.com/br/app/seu-app/id123456789')}
            >
              Copiar Link
            </button>
          </div>
        </div>
      </div>
      
      {/* Outras Configurações */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-none shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Outras Configurações</h3>
        
        <div className="space-y-4">
          <button 
            onClick={() => setChangePasswordModal(true)}
            className="w-full text-left p-3 rounded-none border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <h4 className="font-medium text-gray-800 dark:text-white">Alterar Senha</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Atualize sua senha de acesso</p>
          </button>
          
          <button className="w-full text-left p-3 rounded-none border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors opacity-50 cursor-not-allowed">
            <h4 className="font-medium text-gray-800 dark:text-white">Privacidade e Segurança</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Configure suas preferências de privacidade</p>
          </button>
          
          <button 
            onClick={() => setDeleteAccountModal(true)}
            className="w-full text-left p-3 rounded-none border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900 transition-colors text-red-600 dark:text-red-400"
          >
            <h4 className="font-medium">Excluir Conta</h4>
            <p className="text-sm">Remover permanentemente sua conta e todos os dados</p>
          </button>
        </div>
      </div>

      {/* Modal de Alteração de Senha */}
      {changePasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-none shadow-lg w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Alterar Senha</h3>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Senha Atual
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    required
                    minLength={6}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirmar Nova Senha
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setChangePasswordModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-none hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-none hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Conta */}
      {deleteAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-none shadow-lg w-full max-w-md">
            <div className="p-6">
              <div className="bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-none p-4 mb-4">
                <h3 className="text-xl font-semibold text-red-800 dark:text-red-300 mb-2 flex items-center gap-2">
                  <FiAlertTriangle className="w-5 h-5" /> Atenção!
                </h3>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  Esta ação é irreversível. Todos os seus aplicativos e dados serão permanentemente excluídos.
                </p>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-none p-4 mb-4">
                <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2 flex items-center gap-2">
                  <FiDollarSign className="w-5 h-5" /> Valores a Negociar
                </h4>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                  Se você possui valores investidos em aplicativos, entre em contato com nossa equipe 
                  para negociar possíveis reembolsos antes de excluir sua conta.
                </p>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-2 flex items-center gap-2">
                  <FiPhoneCall className="w-4 h-4" /> Contato: criaappoficial@gmail.com
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Digite "excluir minha conta" para confirmar:
                </label>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-none focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                  placeholder="excluir minha conta"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteAccountModal(false);
                    setDeleteConfirm('');
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-none hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading || deleteConfirm.toLowerCase() !== 'excluir minha conta'}
                  className="px-4 py-2 bg-red-600 text-white rounded-none hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Excluindo...' : 'Excluir Conta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;